/**
 * Linked tasks on a task page: the parent it's part of, its sub-tasks (with progress, tick to finish, add in
 * one line) and tasks it's related to. "Link a task" searches by name or WRK number.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowDown, ArrowUp, Check, CornerLeftUp, GripVertical, Link2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api, del, patch, post, put } from "../lib/api";
import type { Item } from "../lib/work";
import { Card, ErrorNote } from "./ui";

interface Mini { id: string; ref: string; title: string; state: string }
const closed = (s: string) => s === "done" || s === "cancelled";

function Row({ t, onRemove, onTick, removeLabel, onRename, onDelete, onMove, drag }: { t: Mini; onRemove?: () => void; onTick?: () => void; removeLabel: string; onRename?: (title: string) => void; onDelete?: () => void; onMove?: (dir: -1 | 1) => void; drag?: { over: boolean; onStart: () => void; onOver: () => void; onDrop: () => void; onEnd: () => void } }) {
  const [editing, setEditing] = useState<string | null>(null);
  const commit = () => { const v = (editing ?? "").trim(); setEditing(null); if (onRename && v && v !== t.title) onRename(v); };
  return (
    <li className={clsx("group flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-black/[0.03]", drag?.over && "border-t-2 border-brand-500")}
      draggable={!!drag} onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; drag?.onStart(); }} onDragOver={(e) => { if (drag) { e.preventDefault(); drag.onOver(); } }} onDrop={(e) => { e.preventDefault(); drag?.onDrop(); }} onDragEnd={drag?.onEnd}>
      {drag && <GripVertical size={13} className="muted shrink-0 cursor-grab opacity-0 group-hover:opacity-100" aria-hidden />}
      {onMove && (
        <span className="flex shrink-0 flex-col opacity-0 group-hover:opacity-100">
          <button aria-label="Move up" title="Move up" onClick={() => onMove(-1)}><ArrowUp size={11} className="muted" /></button>
          <button aria-label="Move down" title="Move down" onClick={() => onMove(1)}><ArrowDown size={11} className="muted" /></button>
        </span>
      )}
      {onTick ? (
        <button aria-label={`Finish ${t.title}`} disabled={closed(t.state)} onClick={onTick}
          className={clsx("grid h-[18px] w-[18px] shrink-0 place-items-center rounded border-[1.5px]", closed(t.state) ? "border-emerald-600 bg-emerald-600 text-white" : "border-[var(--muted)] hover:border-emerald-600")}>
          {closed(t.state) && <Check size={12} />}
        </button>
      ) : <Link2 size={14} className="muted shrink-0" />}
      {editing !== null ? (
        <input className="input !min-h-0 min-w-0 flex-1 !py-0.5 text-sm" autoFocus value={editing} aria-label="Edit sub-task" onChange={(e) => setEditing(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(null); }} />
      ) : (
        <>
          <Link to="/work/$id" params={{ id: t.id }} className={clsx("min-w-0 flex-1 truncate text-sm", closed(t.state) ? "muted line-through" : "font-medium hover:underline")}>{t.title}</Link>
          {onRename && <button className="shrink-0 opacity-50 hover:opacity-100" aria-label={`Edit ${t.title}`} title="Edit" onClick={() => setEditing(t.title)}><Pencil size={13} className="muted" /></button>}
        </>
      )}
      <span className="muted num shrink-0 text-xs">{t.ref}</span>
      {onRemove && <button className="opacity-0 group-hover:opacity-100" aria-label={removeLabel} title={removeLabel} onClick={onRemove}><X size={13} className="muted" /></button>}
      {onDelete && <button className="opacity-0 group-hover:opacity-100" aria-label="Delete" title="Delete" onClick={onDelete}><Trash2 size={13} className="muted hover:text-red-600" /></button>}
    </li>
  );
}

export function LinkedTasks({ itemId, parent, children, related, canEdit }: { itemId: string; parent: Mini | null; children: Mini[]; related: Mini[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const [sub, setSub] = useState("");
  const [finding, setFinding] = useState(false);
  const [q, setQ] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const reorder = (ids: string[]) => void run(() => post(`/api/work/items/${itemId}/subtasks/order`, { ids }));
  const dropOn = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = children.map((x) => x.id).filter((x) => x !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId); // the dragged one lands where the target was; the target moves down
    reorder(ids);
  };
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["item"] }); void qc.invalidateQueries({ queryKey: ["items"] }); };
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); refresh(); } catch (e) { setError(e); }
  };
  const search = useQuery({ queryKey: ["link-search", q], queryFn: () => api<{ items: Item[] }>(`/api/work/items?view=all&q=${encodeURIComponent(q)}&limit=8`), enabled: finding && q.trim().length >= 2 });
  const known = new Set([itemId, parent?.id, ...children.map((c) => c.id), ...related.map((r) => r.id)].filter(Boolean));
  const done = children.filter((c) => closed(c.state)).length;
  const empty = !parent && !children.length && !related.length;

  return (
    <Card title="Linked tasks" icon={<Link2 size={16} />} actions={canEdit && <button className="btn btn-sm" onClick={() => setFinding((x) => !x)}><Plus size={14} /> Link a task</button>}>
      <div className="space-y-3">
        <ErrorNote error={error} />
        {parent && (
          <p className="flex items-center gap-2 text-sm">
            <CornerLeftUp size={14} className="muted" /><span className="muted">Part of</span>
            <Link to="/work/$id" params={{ id: parent.id }} className="min-w-0 truncate font-medium hover:underline">{parent.title}</Link>
            {canEdit && <button className="muted text-xs hover:underline" onClick={() => void run(() => put(`/api/work/items/${itemId}/parent`, { parentId: null }))}>remove</button>}
          </p>
        )}

        {(children.length > 0 || canEdit) && (
          <div>
            <div className="mb-1 flex items-center gap-2">
              <h3 className="section-label">Sub-tasks{children.length ? ` · ${done}/${children.length}` : ""}</h3>
              {children.length > 0 && <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${(done / children.length) * 100}%` }} /></span>}
            </div>
            <ul>
              {children.map((c, n) => (
                <Row key={c.id} t={c} removeLabel="Take out of this task"
                  onTick={canEdit ? () => void run(() => post(`/api/work/items/${c.id}/logs`, { outcome: "done" })) : undefined}
                  onRemove={canEdit ? () => void run(() => put(`/api/work/items/${c.id}/parent`, { parentId: null })) : undefined}
                  onRename={canEdit ? (title) => void run(() => patch(`/api/work/items/${c.id}`, { title })) : undefined}
                  onDelete={canEdit ? () => { if (confirm(`Delete "${c.title}"?`)) void run(() => del(`/api/work/items/${c.id}`)); } : undefined}
                  onMove={canEdit && children.length > 1 ? (dir) => { const to = n + dir; if (to < 0 || to >= children.length) return; const ids = children.map((x) => x.id); [ids[n], ids[to]] = [ids[to]!, ids[n]!]; reorder(ids); } : undefined}
                  drag={canEdit && children.length > 1 ? { over: overId === c.id && dragId !== c.id, onStart: () => setDragId(c.id), onOver: () => setOverId(c.id), onDrop: () => { dropOn(c.id); setDragId(null); setOverId(null); }, onEnd: () => { setDragId(null); setOverId(null); } } : undefined} />
              ))}
            </ul>
            {canEdit && (
              <form className="mt-1 flex items-center gap-2 px-2" onSubmit={(e) => { e.preventDefault(); const t = sub.trim(); if (!t) return; setSub(""); void run(() => post("/api/tasks/quick", { text: t, parentId: itemId })); }}>
                <Plus size={14} className="muted shrink-0" />
                <input className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none" placeholder="Add a sub-task… (dates, @person, !high work too)" value={sub} onChange={(e) => setSub(e.target.value)} />
              </form>
            )}
          </div>
        )}

        {related.length > 0 && (
          <div>
            <h3 className="section-label mb-1">Related</h3>
            <ul>{related.map((r) => <Row key={r.id} t={r} removeLabel="Unlink" onRemove={canEdit ? () => void run(() => del(`/api/work/items/${itemId}/related/${r.id}`)) : undefined} />)}</ul>
          </div>
        )}

        {finding && (
          <div className="rounded-xl border border-[var(--line)] p-2">
            <div className="flex items-center gap-2 px-1">
              <Search size={14} className="muted" />
              <input autoFocus className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none" placeholder="Find a task by name or WRK-…" value={q} onChange={(e) => setQ(e.target.value)} />
              <button onClick={() => { setFinding(false); setQ(""); }} aria-label="Close"><X size={14} className="muted" /></button>
            </div>
            {q.trim().length >= 2 && (
              <ul className="mt-1 max-h-64 overflow-auto">
                {(search.data?.items ?? []).filter((x) => !known.has(x.id)).map((x) => (
                  <li key={x.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-black/[0.03]">
                    <span className="min-w-0 flex-1 truncate">{x.title} <span className="muted num text-xs">{x.ref}</span></span>
                    <button className="btn btn-sm" onClick={() => void run(() => put(`/api/work/items/${itemId}/related/${x.id}`, {}))}>Related</button>
                    <button className="btn btn-sm" onClick={() => void run(() => put(`/api/work/items/${x.id}/parent`, { parentId: itemId }))}>Make sub-task</button>
                  </li>
                ))}
                {search.data && !search.data.items.filter((x) => !known.has(x.id)).length && <li className="muted px-2 py-1.5 text-sm">No other task matches.</li>}
              </ul>
            )}
          </div>
        )}
        {empty && !finding && !canEdit && <p className="muted text-sm">No linked tasks.</p>}
      </div>
    </Card>
  );
}
